import { CARD, HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  type Breakdown,
  displayForm,
  type PartKind,
  type RootEntry,
  type WordPart,
} from "@/lib/tool-kit/word-knowledge/roots";

export interface EntryCardStrings {
  kindNames: Record<PartKind, string>;
  originNames: Record<RootEntry["origin"], string>;
  sourceLine: string;
  examplesLabel: string;
  alsoSpelled: string;
}

const BADGE =
  "rounded bg-primary/10 px-1.5 py-0.5 font-normal text-primary text-xs";

/** One root, prefix or suffix: spellings, meaning, source word and clickable examples. */
export function EntryCard({
  entry,
  strings,
  onBreak,
}: {
  entry: RootEntry;
  strings: EntryCardStrings;
  onBreak: (word: string) => void;
}) {
  const [main, ...others] = entry.forms;
  return (
    <article className={`${CARD} space-y-2`}>
      <h3 className={`${WIDGET_H3} flex flex-wrap items-baseline gap-x-2`}>
        <span>{displayForm(entry, main)}</span>
        <span className={BADGE}>{strings.kindNames[entry.kind]}</span>
        <span className={BADGE}>{strings.originNames[entry.origin]}</span>
      </h3>
      {others.length > 0 ? (
        <p className={HINT}>
          {t(strings.alsoSpelled, {
            forms: others.map((form) => displayForm(entry, form)).join(", "),
          })}
        </p>
      ) : null}
      <p className="text-foreground text-sm">{entry.meaning}</p>
      <p className={HINT}>{t(strings.sourceLine, { source: entry.source })}</p>
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="text-muted-foreground text-xs">
          {strings.examplesLabel}
        </span>
        {entry.examples.map((example) => (
          <button
            className="text-primary underline-offset-4 hover:underline"
            key={example}
            onClick={() => onBreak(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </p>
    </article>
  );
}

export interface BreakdownStrings {
  partLabels: Record<PartKind | "glue" | "unknown", string>;
  helperLabels: Record<"prefix" | "suffix", string>;
  glueMeaning: string;
  unknownMeaning: string;
  alternativeNote: string;
  summaryLabel: string;
  coverageNote: string;
  caution: string;
}

function partMeaning(part: WordPart, strings: BreakdownStrings): string {
  if (part.kind === "glue") {
    return strings.glueMeaning;
  }
  return part.entry ? part.entry.meaning : strings.unknownMeaning;
}

function partCaption(part: WordPart, strings: BreakdownStrings): string {
  if (!(part.entry && part.kind !== "glue" && part.kind !== "unknown")) {
    return strings.partLabels[part.kind];
  }
  if (part.entry.helper) {
    return strings.helperLabels[part.kind === "prefix" ? "prefix" : "suffix"];
  }
  return strings.partLabels[part.kind];
}

function PartCard({
  part,
  strings,
}: {
  part: WordPart;
  strings: BreakdownStrings;
}) {
  const known = part.entry !== null && !part.entry.helper;
  return (
    <li className={`${CARD} space-y-1 ${known ? "" : "border-dashed"}`}>
      <p className="font-semibold text-foreground text-xl">
        {part.kind === "unknown" ? `${part.text}?` : part.text}
      </p>
      <p className={HINT}>
        {partCaption(part, strings)}
        {part.entry && part.form
          ? ` · ${displayForm(part.entry, part.form)}`
          : ""}
      </p>
      <p className="text-foreground text-sm">{partMeaning(part, strings)}</p>
      {part.alternatives.length > 0 ? (
        <p className={HINT}>
          {t(strings.alternativeNote, {
            others: part.alternatives.map((entry) => entry.meaning).join("; "),
          })}
        </p>
      ) : null}
    </li>
  );
}

/** The parts of one word, the sum of their meanings, and the honest caveat. */
export function BreakdownView({
  breakdown,
  strings,
}: {
  breakdown: Breakdown;
  strings: BreakdownStrings;
}) {
  const summary = breakdown.parts
    .filter((part) => part.entry && !part.entry.helper)
    .map((part) => part.entry?.meaning.split(";")[0] ?? "")
    .join(" + ");
  return (
    <div className="space-y-4">
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {breakdown.parts.map((part, position) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional, never reordered
          <PartCard key={position} part={part} strings={strings} />
        ))}
      </ul>
      {summary ? (
        <p className="text-foreground text-sm">
          <span className="font-medium">{strings.summaryLabel}</span> {summary}
        </p>
      ) : null}
      {breakdown.coverage < 1 ? (
        <p className={HINT}>{strings.coverageNote}</p>
      ) : null}
      <p className={HINT}>{strings.caution}</p>
    </div>
  );
}
